<template lang="pug">
.ep-overlay(v-if="visible" @click.self="onCancel")
  .ep-modal(role="dialog" aria-modal="true" aria-labelledby="ep-modal-title")

    //- ── Header ─────────────────────────────────────────────────────────────
    .ep-modal__header
      span#ep-modal-title.ep-modal__title Enviar póliza al cliente
      button.ep-modal__close-btn(@click="onCancel" type="button" aria-label="Cerrar")
        b-icon(icon="x-lg")

    //- ── Error alert ────────────────────────────────────────────────────────
    template(v-if="hasError")
      .ep-modal__error-block
        b-icon.ep-modal__alert-icon(icon="exclamation-circle-fill")
      p.ep-modal__desc Ocurrió un problema al intentar enviar la póliza al cliente. Verifica tu conexión e intenta nuevamente.
    template(v-else)
      p.ep-modal__desc Adjunta el documento oficial. Al confirmar, este archivo se enviará automáticamente al correo del cliente y esta OVG quedará cerrada.

    //- ── Upload form ────────────────────────────────────────────────────────
    .ep-form
      span.ep-form__label Adjuntar Póliza

      //- Dropzone vacío
      .ep-dropzone(
        v-if="!polizaFile"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
        @drop.prevent="onDrop"
        :class="{ 'ep-dropzone--active': isDragOver }"
      )
        b-icon.ep-dropzone__cloud-icon(icon="cloud-upload-fill" font-scale="2.5")
        p.ep-dropzone__text Arrastra y suelta tu archivo aquí.
        button.ep-dropzone__select-btn(@click="$refs.fileInput.click()" type="button")
          | Seleccionar desde tu equipo
        small.ep-dropzone__hint Formatos aceptados: PDF. Peso máximo: 5 MB.

      //- Preview de archivo seleccionado
      .ep-dropzone.ep-dropzone--with-file(v-else)
        .ep-file-card
          .ep-file-card__row
            b-icon.ep-file-card__pdf-icon(icon="file-earmark-pdf-fill" variant="danger" font-scale="1.5")
            span.ep-file-card__name {{ polizaFile.name }}
            button.ep-file-card__delete-btn(
              @click="removeFile"
              type="button"
              :disabled="loading"
              aria-label="Eliminar archivo"
            )
              b-icon(icon="trash" font-scale="1.5")
          span.ep-file-card__size {{ fileSize }}

      input(
        ref="fileInput"
        type="file"
        accept=".pdf"
        style="display:none"
        @change="onFileChange"
      )

    //- ── Footer ─────────────────────────────────────────────────────────────
    .ep-modal__footer
      button.ep-modal__btn-cancel(
        type="button"
        :disabled="loading"
        @click="onCancel"
      ) Cancelar
      button.ep-modal__btn-confirm(
        type="button"
        :disabled="!polizaFile || loading"
        @click="$emit('confirm', polizaFile)"
      )
        b-spinner(v-if="loading" small)
        span(v-else) {{ hasError ? 'Reintentar Envío' : 'Enviar póliza y cerrar' }}
</template>

<script>
export default {
  name: 'EnviarPolizaModal',

  model: {
    prop: 'visible',
    event: 'update:visible',
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      polizaFile: null,
      isDragOver: false,
    }
  },

  computed: {
    fileSize() {
      if (!this.polizaFile) return ''
      const kb = Math.round(this.polizaFile.size / 1024)
      return `${kb} kb.`
    },
  },

  watch: {
    visible(val) {
      if (!val) this.resetState()
    },
  },

  methods: {
    onCancel() {
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },

    onDrop(e) {
      this.isDragOver = false
      const file = e.dataTransfer && e.dataTransfer.files[0]
      if (!file) return
      if (file.type !== 'application/pdf') return
      if (file.size > 5 * 1024 * 1024) return
      this.polizaFile = file
    },

    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.polizaFile = file
      // reset input para permitir seleccionar el mismo archivo de nuevo
      e.target.value = ''
    },

    removeFile() {
      this.polizaFile = null
    },

    resetState() {
      this.polizaFile = null
      this.isDragOver = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/style/colors.scss';

// ── Overlay ──────────────────────────────────────────────────────────────────
.ep-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

// ── Modal card ───────────────────────────────────────────────────────────────
.ep-modal {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  width: 520px;
  max-width: calc(100vw - 32px);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// ── Header ───────────────────────────────────────────────────────────────────
.ep-modal__header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.ep-modal__title {
  font-family: 'Noto Sans', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #23201c;
  line-height: 1.3;
}

.ep-modal__close-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #23201c;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  &:hover { opacity: 0.7; }
}

// ── Error block ──────────────────────────────────────────────────────────────
.ep-modal__error-block {
  display: flex;
  justify-content: center;
  align-items: center;
}

.ep-modal__alert-icon {
  font-size: 74px;
  color: #dc3545;
}

// ── Description ──────────────────────────────────────────────────────────────
.ep-modal__desc {
  font-family: 'Noto Sans', sans-serif;
  font-weight: 400;
  font-size: 15px;
  color: #525252;
  margin: 0;
  line-height: 1.5;
}

// ── Form section ─────────────────────────────────────────────────────────────
.ep-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
}

.ep-form__label {
  font-family: 'Noto Sans', sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #23201c;
}

// ── Dropzone ─────────────────────────────────────────────────────────────────
.ep-dropzone {
  border: 1px dashed #525252;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 0;
  transition: border-color 0.2s, background-color 0.2s;

  &--active {
    border-color: $samtek-yellow;
    background-color: #fffbea;
  }

  &--with-file {
    padding: 20px;
    align-items: stretch;
    justify-content: flex-start;
  }
}

.ep-dropzone__cloud-icon {
  color: #1a73e8;
  margin-bottom: 2px;
}

.ep-dropzone__text {
  font-family: 'Noto Sans', sans-serif;
  font-weight: 400;
  font-size: 15px;
  color: #525252;
  text-align: center;
  margin: 0;
}

.ep-dropzone__select-btn {
  width: 242px;
  height: 35px;
  background-color: $samtek-yellow;
  border: none;
  border-radius: 8px;
  font-family: 'Noto Sans', sans-serif;
  font-weight: 500;
  font-size: 15px;
  color: #23201c;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: darken(#fece2f, 8%);
  }
}

.ep-dropzone__hint {
  font-family: 'Noto Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #525252;
  text-align: center;
}

// ── File card ─────────────────────────────────────────────────────────────────
.ep-file-card {
  background-color: #f5f6fa;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ep-file-card__row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.ep-file-card__pdf-icon {
  flex-shrink: 0;
}

.ep-file-card__name {
  font-family: 'Noto Sans', sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #23201c;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ep-file-card__delete-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #525252;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  &:hover { color: #dc3545; }
  &:disabled { opacity: 0.5; cursor: default; }
}

.ep-file-card__size {
  font-family: 'Noto Sans', sans-serif;
  font-style: italic;
  font-weight: 400;
  font-size: 15px;
  color: #525252;
}

// ── Footer ───────────────────────────────────────────────────────────────────
.ep-modal__footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 45px;
}

.ep-modal__btn-cancel {
  background-color: #ffffff;
  border: 1px solid $samtek-yellow;
  border-radius: 8px;
  padding: 10px 20px;
  height: 45px;
  font-family: 'Noto Sans', sans-serif;
  font-weight: 500;
  font-size: 15px;
  color: #23201c;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #fffbea;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.ep-modal__btn-confirm {
  background-color: $samtek-yellow;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  height: 45px;
  font-family: 'Noto Sans', sans-serif;
  font-weight: 500;
  font-size: 15px;
  color: #23201c;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s, opacity 0.2s;

  &:hover:not(:disabled) {
    background-color: darken(#fece2f, 8%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}
</style>
